import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import ErrorConfirmation from '@/components/ErrorConfirmation'
import { Plus } from '@/components/Icons'
import Page from '@/components/Page'
import { fetchRoles, fetchUsersPaginated, resetRequest } from '@/store/actions'
import { getCurrentUser, isRequestComplete, isRequestError, isRequestPending } from '@/store/selectors'

import { deleteUser, resetPaginationState, setEditUser, setFilter, setSortingColumn } from './actions'
import { DELETE_USER, FETCH_ORG_USERS } from './actionTypes'
import messages from './messages'
import OrganizationUsers from './OrganizationUsers'
import {
  getFilter,
  getIsEditingUser,
  getSortingColumn,
  getStartIndex,
  getTotalUsers,
  mapPaginatedUsers
} from './selectors'

import './index.scss'

import { useHistory, useLocation } from 'react-router-dom'

import DropDownButton from '@/components/buttons/DropDownButton'
import DropDownItem from '@/components/buttons/DropDownItem'
import FeatureToggle from '@/components/FeatureToggle'
import { USER_ATTRIBUTES } from '@/components/User'

import ExportAllUsers from './ExportAllUsers'

const SORT_ORDER_MAP = {
  ASC: 'ascending',
  DESC: 'descending'
}

function OrganizationUsersPage(props: any) {
  const intl = useIntl()

  const history = useHistory()
  const location = useLocation()

  const [state, _setState] = useState({
    initialLoaded: false,
    usersPerPage: 10
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidMount
  useEffect(() => {
    props.actions.resetRequest([DELETE_USER])

    if (isEmpty(props.orgAdminRole)) {
      props.actions.fetchRoles()
    }

    if (props.isEditingUser) {
      props.actions.setEditUser(false)
    } else {
      props.actions.resetPaginationState()
    }

    setState({ initialLoaded: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navigateToAddUser = () => {
    history.push(location.pathname + '/create')
  }

  const navigateToBulkAddUser = () => {
    history.push(location.pathname + '/bulk')
  }

  const handleAddUser = (value: any) => {
    if (value === 'bulk') {
      navigateToBulkAddUser()
    } else {
      navigateToAddUser()
    }
  }

  const navigateToEditUser = (userId: any) => {
    props.actions.setEditUser(true)
    history.push(location.pathname + '/' + userId + '/edit')
  }

  const loadPage = (page: any, sortBy: any, sortOrder: any, filter: any) => {
    props.actions.setSortingColumn(sortBy, sortOrder)
    props.actions.setFilter(filter)

    const query: {
      attributes: string[]
      count: number
      sortOrder: string
      sortBy: any
      startIndex: number
      filter?: string
    } = {
      attributes: USER_ATTRIBUTES,
      count: state.usersPerPage,
      // @ts-expect-error
      sortOrder: SORT_ORDER_MAP[sortOrder || ''],
      sortBy: sortBy,
      startIndex: (page - 1) * state.usersPerPage + 1
    }

    if (filter) {
      query.filter = USER_ATTRIBUTES.map(attribute => `${attribute} co ${JSON.stringify(filter)}`).join(' or ')
    }

    props.actions.fetchUsersPaginated(query, FETCH_ORG_USERS)
  }

  const removeUserFromOrganization = (userId: any) => {
    props.actions.deleteUser(userId)
  }

  const {
    currentUser,
    hasError,
    users,
    startIndex,
    totalItemsCount,
    isLoadingUsers,
    isLoadingUsersComplete,
    isUserDeleted,
    isUserDeleting,
    sortingColumn
  } = props
  return (
    <Page className="organization-users-wrapper">
      <Page.Header
        title={<FormattedMessage {...messages.pageTitle} />}
        buttons={
          <div style={{ display: 'flex' }}>
            <FeatureToggle feature="export-all-users">
              <ExportAllUsers />
            </FeatureToggle>

            <DropDownButton
              key="add-user"
              onSelected={handleAddUser}
              className="add-user-button"
              title={
                <>
                  <Plus /> <FormattedMessage {...messages.addUser} />
                </>
              }
            >
              <DropDownItem value="single">
                <span className="dropdown-text">
                  <FormattedMessage {...messages.addSingleUser} />
                </span>
              </DropDownItem>
              <DropDownItem value="bulk">
                <span className="dropdown-text">
                  <FormattedMessage {...messages.bulkAddUser} />
                </span>
              </DropDownItem>
            </DropDownButton>
          </div>
        }
      />
      {hasError ? (
        <ErrorConfirmation
          error={intl.formatMessage(messages.errorTitle)}
          subMessage={intl.formatMessage(messages.errorMessage)}
        />
      ) : (
        state.initialLoaded && (
          <OrganizationUsers
            currentUser={currentUser}
            filterValue={props.filterValue}
            userCount={totalItemsCount}
            startIndex={startIndex}
            loadPage={loadPage}
            loadInitialPage={isUserDeleted}
            isLoading={isLoadingUsers}
            isDeleting={isUserDeleting}
            isLoadingUsersComplete={isLoadingUsersComplete}
            sortingColumn={sortingColumn}
            users={users}
            perPage={state.usersPerPage}
            navigateToEditUser={navigateToEditUser}
            onRemoveFromOrganization={removeUserFromOrganization}
          />
        )
      )}
    </Page>
  )
}

OrganizationUsersPage.displayName = 'OrganizationUsersPage'

OrganizationUsersPage.propTypes = {
  actions: PropTypes.shape({
    resetPaginationState: PropTypes.func.isRequired,
    resetRequest: PropTypes.func.isRequired,
    setEditUser: PropTypes.func.isRequired,
    setFilter: PropTypes.func.isRequired,
    setSortingColumn: PropTypes.func.isRequired,
    fetchUsersPaginated: PropTypes.func.isRequired,
    deleteUser: PropTypes.func.isRequired
  }).isRequired,
  currentUser: PropTypes.object,
  filterValue: PropTypes.string,
  startIndex: PropTypes.number.isRequired,
  hasError: PropTypes.bool,
  isLoadingUsers: PropTypes.bool,
  isLoadingUsersComplete: PropTypes.bool,
  isUserDeleted: PropTypes.bool.isRequired,
  isUserDeleting: PropTypes.bool,
  isEditingUser: PropTypes.bool,
  sortingColumn: PropTypes.object,
  totalItemsCount: PropTypes.number.isRequired,
  users: PropTypes.array
}

OrganizationUsersPage.defaultProps = {
  currentUser: null,
  filterValue: '',
  hasError: false,
  isLoadingUsers: false,
  isLoadingUsersComplete: false,
  isEditingUser: false,
  isUserDeleting: false,
  sortingColumn: null,
  users: []
}

const DisconnectedOrganizationUsersPage = OrganizationUsersPage
export { /** @deprecated */ DisconnectedOrganizationUsersPage as OrganizationUsersPage }

function mapStateToProps(state: any) {
  return {
    currentUser: getCurrentUser(state),
    filterValue: getFilter(state),
    hasError: isRequestError(state, [DELETE_USER]),
    isLoadingUsers: isRequestPending(state, FETCH_ORG_USERS),
    isLoadingUsersComplete: isRequestComplete(state, FETCH_ORG_USERS),
    isUserDeleted: isRequestComplete(state, DELETE_USER),
    isUserDeleting: isRequestPending(state, DELETE_USER),
    isEditingUser: getIsEditingUser(state),
    sortingColumn: getSortingColumn(state),
    users: mapPaginatedUsers(state),
    totalItemsCount: getTotalUsers(state),
    startIndex: getStartIndex(state)
  }
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators(
      {
        deleteUser,
        fetchRoles,
        fetchUsersPaginated,
        resetPaginationState,
        resetRequest,
        setEditUser,
        setFilter,
        setSortingColumn
      },
      dispatch
    )
  }
}
export default connect(mapStateToProps, mapDispatchToProps)(DisconnectedOrganizationUsersPage)
